/**
 * Product page CTA:
 * - Default "select size" until a size is chosen (unless ?variant= is in the URL)
 * - Error message if CTA is clicked before size selection
 * - Low-stock copy after the signup CTA, above Add to Bag
 */
(() => {
  function selectSizeLabel() {
    return window.themeVariables?.strings?.selectSizeButton || 'select size';
  }

  function selectSizeError() {
    return window.themeVariables?.strings?.selectSizeError || 'Please select your size above';
  }

  function sizeLabelList() {
    const fromTheme = window.themeVariables?.strings?.sizeOptionLabels;
    if (fromTheme) {
      return String(fromTheme)
        .toLowerCase()
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return ['size', 'sizes'];
  }

  function getSizeOptionPosition(form) {
    const labels = sizeLabelList();
    const fieldsets = form.closest('.shopify-section--main-product')?.querySelectorAll('variant-picker fieldset.variant-picker__option') || [];
    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend');
      const name = (legend?.textContent || '').replace(':', '').trim().toLowerCase();
      if (labels.includes(name)) {
        const input = fieldset.querySelector('input[data-option-position]');
        return input ? input.getAttribute('data-option-position') : null;
      }
    }
    return null;
  }

  function getSizeInputs(form, position) {
    if (!position) return [];
    const section = form.closest('.shopify-section--main-product');
    if (!section) return [];
    return Array.from(section.querySelectorAll(`input[data-option-position="${position}"]`)).filter(
      (input) => input.getAttribute('form') === form.id || input.form === form
    );
  }

  function getSubmitButton(form) {
    // Only the ATC — never the BIS drawer submit.
    return form.querySelector('.product-cta__atc, buy-buttons button.product-cta__atc[type="submit"]')
      || form.querySelector('buy-buttons button[type="submit"]');
  }

  function setButtonLabel(button, label) {
    if (!button) return;
    const content = button.querySelector('button-content') || button;
    content.textContent = label;
    if (content.hasAttribute('rotated-text')) content.setAttribute('rotated-text', label);
    if (content.hasAttribute('up-text')) content.setAttribute('up-text', label);
  }

  function getVariantsData(form) {
    const script = form.querySelector('[data-cta-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  }

  function effectiveQty(variant) {
    if (!variant || !variant.inventory_management) return null;
    let qty = Number(variant.inventory_quantity);
    if (Number.isNaN(qty)) return null;
    // Oversell / preorder-style: treat 0 as "Low Stock" (effective 3)
    if (variant.inventory_policy === 'continue' && qty <= 0) qty = 3;
    return qty;
  }

  function lowStockMessage(qty) {
    if (qty === 1) return "There's only 1 item left - be quick";
    if (qty === 2) return "There's only 2 items left - be quick";
    if (qty >= 3 && qty <= 5) return 'Low Stock';
    return '';
  }

  function updateLowStock(form, variant) {
    const el = form.querySelector('[data-low-stock]');
    if (!el) return;

    if (form.dataset.sizeSelected !== 'true') {
      el.hidden = true;
      el.textContent = '';
      return;
    }

    const message = lowStockMessage(effectiveQty(variant));
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function updatePreOrder(form, variant) {
    const text = form.querySelector('[data-pre-order-text]');
    const shippingInput = form.querySelector('[data-expected-shipping]');
    const isPreorder = Boolean(variant?.is_preorder && variant?.available);
    const shipping = variant?.expected_shipping || '';

    if (text) {
      text.textContent = shipping;
      text.hidden = !(isPreorder && shipping);
    }

    if (shippingInput) {
      shippingInput.value = shipping;
      shippingInput.disabled = !(isPreorder && shipping);
    }
  }

  function applySelectSizeState(form) {
    const button = getSubmitButton(form);
    if (!button) return;
    button.disabled = false;
    button.hidden = false;
    button.removeAttribute('data-bis-hidden');
    setButtonLabel(button, selectSizeLabel());
    const error = form.querySelector('[data-select-size-error]');
    if (error) error.hidden = true;
    updateLowStock(form, null);
    const text = form.querySelector('[data-pre-order-text]');
    if (text) text.hidden = true;
    const shippingInput = form.querySelector('[data-expected-shipping]');
    if (shippingInput) shippingInput.disabled = true;
    const notify = form.querySelector('.timesact-button-bis, .product-cta__notify');
    if (notify) {
      notify.hidden = true;
      notify.removeAttribute('data-bis-active');
    }
    window.NookieTimesactBis?.sync?.(form, null, { sizeSelected: false });
  }

  function clearSizeSelection(form, position) {
    getSizeInputs(form, position).forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
      const control = input.nextElementSibling;
      if (control?.classList?.contains('is-selected')) {
        control.classList.remove('is-selected');
      }
    });
    const fieldset = getSizeInputs(form, position)[0]?.closest('fieldset');
    const valueLabel = fieldset?.querySelector('.variant-picker__option-info span:last-child');
    if (valueLabel) valueLabel.textContent = '';
  }

  function findVariant(form) {
    const id = form.querySelector('[name="id"]')?.value;
    return getVariantsData(form).find((v) => String(v.id) === String(id)) || null;
  }

  function applyVariantState(form, variant) {
    const button = getSubmitButton(form);
    if (!button) return;

    if (form.dataset.sizeSelected !== 'true') {
      applySelectSizeState(form);
      return;
    }

    const strings = window.themeVariables?.strings || {};
    let label = strings.addToCartButton || 'Add to Bag';
    let disabled = false;
    const bisActive =
      !variant?.available &&
      variant?.id &&
      window.NookieTimesactBis?.isBisActive?.(variant.id);

    if (!variant?.id) {
      label = strings.unavailableButton || 'Unavailable';
      disabled = true;
    } else if (bisActive) {
      // Timesact BIS takes over the CTA — Notify me button is shown by timesact-bis.js
      label = strings.soldOutButton || 'Out of stock';
      disabled = true;
    } else if (!variant.available) {
      label = strings.soldOutButton || 'Out of stock';
      disabled = true;
    } else if (variant.is_preorder) {
      label = strings.preOrderButton || 'Pre Order';
    }

    button.disabled = disabled;
    button.hidden = false;
    button.removeAttribute('data-bis-hidden');
    setButtonLabel(button, label);
    updateLowStock(form, variant);
    updatePreOrder(form, variant);
    window.NookieTimesactBis?.sync?.(form, variant, { sizeSelected: true });
  }

  function showSelectSizeError(form) {
    let error = form.querySelector('[data-select-size-error]');
    if (!error) {
      const stack = form.querySelector('.product-cta-stack') || form.querySelector('buy-buttons');
      error = document.createElement('p');
      error.className = 'low-stock-text select-size-error';
      error.setAttribute('data-select-size-error', '');
      const after = stack?.querySelector('[data-low-stock]') || stack?.querySelector('.product-signup-cta');
      if (after) {
        after.after(error);
      } else {
        stack?.prepend(error);
      }
    }
    error.textContent = selectSizeError();
    error.hidden = false;
  }

  function markSizeSelected(form) {
    form.dataset.sizeSelected = 'true';
    const error = form.querySelector('[data-select-size-error]');
    if (error) error.hidden = true;
  }

  function syncAfterVariantUpdate(form, sizePosition, variantId) {
    if (form.dataset.sizeSelected === 'true') {
      const fromJson = variantId
        ? getVariantsData(form).find((v) => String(v.id) === String(variantId))
        : null;
      applyVariantState(form, fromJson || findVariant(form));
      return;
    }

    if (sizePosition) {
      // Partial re-render restores a checked size from Liquid — clear again until the shopper picks one
      clearSizeSelection(form, sizePosition);
    }
    applySelectSizeState(form);
  }

  function initForm(form) {
    if (!form || form.dataset.ctaBound === 'true') return;
    const section = form.closest('.shopify-section--main-product');
    if (!section) return;

    form.dataset.ctaBound = 'true';
    const sizePosition = getSizeOptionPosition(form);
    form.dataset.sizeOptionPosition = sizePosition || '';

    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (sizePosition && !urlVariant) {
      form.dataset.sizeSelected = 'false';
      clearSizeSelection(form, sizePosition);
      applySelectSizeState(form);
    } else {
      form.dataset.sizeSelected = sizePosition ? (urlVariant ? 'true' : 'false') : 'true';
      if (form.dataset.sizeSelected === 'true') {
        applyVariantState(form, findVariant(form));
      } else {
        applySelectSizeState(form);
      }
    }

    form.addEventListener(
      'submit',
      (event) => {
        if (form.dataset.sizeSelected === 'true' || !sizePosition) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        showSelectSizeError(form);
      },
      true
    );

    // Option radios are outside the form element — listen on the section
    section.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.getAttribute('form') !== form.id && input.form !== form) return;

      if (sizePosition && input.getAttribute('data-option-position') === sizePosition && input.checked) {
        markSizeSelected(form);
        requestAnimationFrame(() => {
          applyVariantState(form, findVariant(form));
        });
      }
    });

    form.addEventListener('variant:change', (event) => {
      syncAfterVariantUpdate(form, sizePosition, event.detail?.variant?.id);
    });

    form.addEventListener('product:rerender', () => {
      queueMicrotask(() => {
        syncAfterVariantUpdate(form, sizePosition, form.querySelector('[name="id"]')?.value);
      });
    });
  }

  function initAll() {
    // Exclude the Timesact BIS drawer form — it also lives under product-form / the section
    // and has its own type=submit CTA that must not get "select size" / ATC labels.
    document
      .querySelectorAll('.shopify-section--main-product product-form form[action*="/cart/add"]')
      .forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
